import { Grid, IconButton } from '@mui/material';
import { MoreHoriz as MoreIcon } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { filter, pathEq, pipe, prop, sortBy, values } from 'ramda';
import React, { useContext, useState } from 'react';
import { AppConfigContext } from '../../../hooks/useCaseDefinition';
import { UseCaseDefinition } from '../../../model';
import { getDocumentColor, getDocumentIcon } from '../../common/helpers';
import TileMenuItem from '../../tileMenu/Tile/TileMenuItem';
import ListFooterSidebar from './ListFooterSidebar';

export const FOOTER_HEIGHT = 50;
export const FOOTER_HEIGHT_XS = 65;

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  root: {
    width: '100%',
    rowGap: 2,
    columnGap: 2,
    alignItems: 'center',
  },

  gridItem: {
    flex: '1 1 calc(50% - 27px)',
    height: `${FOOTER_HEIGHT}px`,
    [breakpoints.down('xs')]: {
      height: `${FOOTER_HEIGHT_XS}px`,
    },
  },

  moreButtonGridItem: {
    flex: '0 0 50px',
    maxWidth: '50px',
    height: `${FOOTER_HEIGHT}px`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [breakpoints.down('xs')]: {
      height: `${FOOTER_HEIGHT_XS}px`,
      flex: '0 0 45px',
      maxWidth: '45px',
    },
  },

  moreButtonContainer: {
    flex: '0 0 50px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: `${FOOTER_HEIGHT}px`,
    [breakpoints.down('xs')]: {
      height: `${FOOTER_HEIGHT_XS}px`,
    },
  },

  moreButton: {
    width: 50,
    height: 50,
    border: `1px solid ${palette.divider}`,
    borderRadius: 4,
    backgroundColor: palette.background.paper,
    '&:hover': {
      backgroundColor: palette.action.hover,
    },
    [breakpoints.down('xs')]: {
      width: 45,
      height: 45,
    },
  },

  moreIcon: {
    fontSize: 24,
    color: palette.text.secondary,
  },
}));

type Props = StandardProps & {
  onGenerateTransactionClick: (useCaseDefinition: UseCaseDefinition) => void;
};

const ListFooter: React.FC<Props> = ({ onGenerateTransactionClick, ...others }) => {
  const classes = useStyles({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { visibleUseCaseDefinitions } = useContext(AppConfigContext);

  const openDefinitions = pipe<any, UseCaseDefinition[], UseCaseDefinition[], UseCaseDefinition[]>(
    values,
    sortBy(prop('order')),
    filter(pathEq(['processConfig', 'openList'], true))
  )(visibleUseCaseDefinitions);

  if (openDefinitions.length < 1) {
    return null;
  }

  const visibleDefinitions = openDefinitions.slice(0, 2);
  const hiddenDefinitions = openDefinitions.slice(2);
  const hasMoreItems = hiddenDefinitions.length > 0;

  const handleMoreClick = () => {
    setSidebarOpen(true);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  return (
    <>
      <Grid container className={classNames(classes.root, others.className)}>
        {visibleDefinitions.map((definition) => (
          <Grid item xs key={definition.processId} className={classes.gridItem}>
            <TileMenuItem
              tile={{
                label: definition.name,
                icon: getDocumentIcon(definition),
                color: getDocumentColor(definition),
                fullWidth: false,
              }}
              onClick={() => onGenerateTransactionClick(definition)}
            />
          </Grid>
        ))}

        {hasMoreItems && (
          <Grid item className={classes.moreButtonGridItem}>
            <IconButton className={classes.moreButton} onClick={handleMoreClick} aria-label="More actions">
              <MoreIcon className={classes.moreIcon} />
            </IconButton>
          </Grid>
        )}
      </Grid>

      <ListFooterSidebar
        open={sidebarOpen}
        onClose={handleSidebarClose}
        definitions={hiddenDefinitions}
        onGenerateTransactionClick={onGenerateTransactionClick}
      />
    </>
  );
};

export default ListFooter;
